/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import { literal } from '../util.type';
import Bitfield from './bitfield';

// TODO: Chybí testy (až na stěžejní SomeDaysOfWeek), ale je otázka, zda by
// k nečemu posloužily, když se dědí od třídy Bitfield, která je testovaná.

/**
 * JS Date kompatibilní reprezentace dne v týdnu.
 *
 * Sunday = 0, Monday = 1, ..., Saturday = 6.
 * @enum {0 | 1 | 2 | 3 | 4 | 5 | 6}
 * @readonly
 */
export const DayOfWeek = {
  SUNDAY: literal(0),
  MONDAY: literal(1),
  TUESDAY: literal(2),
  WEDNESDAY: literal(3),
  THURSDAY: literal(4),
  FRIDAY: literal(5),
  SATURDAY: literal(6)
};

/**
 * Pole dnů v týdnů ({@link DayOfWeek}) podle pořadí dnů v týdnů Ki-Wi Serveru.
 * @type {[DayOfWeek, DayOfWeek, DayOfWeek, DayOfWeek, DayOfWeek, DayOfWeek, DayOfWeek]}
 */
export const dayOfWeekArray = [1, 2, 3, 4, 5, 6, 0];

/**
 * JS Date kompatibilní bitové pole dnů v týdnu.
 * @extends {Bitfield<DayOfWeek>}
 */
export class DaysOfWeek extends Bitfield {
  /**
   * @protected
   * @param {Object} o
   * @param {number} o.bitfield Číslo představující bitové pole v rozsahu [0, 2^7).
   * @throws {RangeError} Pokud je {@link o.bitfield} mimo povolený rozsah.
   */
  constructor(o) {
    super({ ...o, n: 7 });

    if (new.target === DaysOfWeek) {
      Object.freeze(this);
    }
  }

  /**
   * Vytvoří bitové pole dnů v týdnu ze zadaného bitfieldu.
   * @param {number} bitfield Číslo představující bitové pole v rozsahu [0, 2^7).
   * @returns {DaysOfWeek}
   * @throws {RangeError} Pokud je {@link bitfield} mimo povolený rozsah.
   */
  static from(bitfield) {
    return new DaysOfWeek({ bitfield });
  }
}

export class SomeDaysOfWeek extends DaysOfWeek {
  /**
   * @protected
   * @param {Object} o
   * @param {number} o.bitfield Číslo představující bitové pole v rozsahu [0, 2^7).
   * @throws {RangeError} Pokud je {@link bitfield} mimo povolený rozsah.
   * @throws {TypeError} Pokud není alespoň jeden den v týdnu nastaven.
   */
  constructor(o) {
    if (o.bitfield === 0) {
      throw new TypeError(
        'SomeDaysOfWeek: at least one day of week must be set'
      );
    }
    super(o);
  }

  /** @override @type {Bitfield['empty']} */
  empty() {
    return false;
  }

  /**
   * Vytvoří bitové pole dnů v týdnu s alespoň jedním nastaveným dnem ze zadaného bitfieldu.
   * @param {number} bitfield Číslo představující bitové pole v rozsahu [0, 2^7).
   * @returns {SomeDaysOfWeek}
   * @throws {RangeError} Pokud je {@link daysOfWeek} mimo povolený rozsah.
   * @throws {TypeError} Pokud není alespoň jeden den v týdnu nastaven.
   */
  static from(bitfield) {
    return new SomeDaysOfWeek({ bitfield });
  }

  /**
   * Vytvoří bitové pole dnů v týdnu s alespoň jedním nastaveným dnem.
   * @param {DaysOfWeek} daysOfWeek
   * @returns {SomeDaysOfWeek}
   * @throws {TypeError} Pokud není alespoň jeden den v týdnu nastaven.
   */
  static fromDaysOfWeek(daysOfWeek) {
    return new SomeDaysOfWeek(daysOfWeek);
  }

  // Zdánlivě zbytečná metoda, ale je nutné mít něco navíc, aby pro TS bylo
  // typově odlišitelné od DaysOfWeek.
  /**
   * Vrací příznak, zda je nějaký den v týdnu nastaven.
   * @returns {true}
   */
  some() {
    return true;
  }
}
